import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import PasswordField from '../components/PasswordField'
import Alert from '../components/Alert'
import OtpInput from '../components/OtpInput'
import Countdown, { useResendGate } from '../components/Countdown'
import Spinner from '../components/Spinner'
import {
  forgetPassword,
  verifyForgetCode,
  setNewPassword,
  pickResetTiming as pickTiming,
  pickResetToken,
} from '../api/auth'
import { authErrorMessage } from '../lib/errors'

export default function ForgotPassword() {
  const [step, setStep] = useState(1)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errors, setErrors] = useState({})
  const [alert, setAlert] = useState(null)
  const [busy, setBusy] = useState(false)
  const [expiresIn, setExpiresIn] = useState(null)
  const [resendAfter, setResendAfter] = useState(null)
  const [startedAt, setStartedAt] = useState(null)
  const [resetToken, setResetToken] = useState(null)
  const [expired, setExpired] = useState(false)

  const resendLeft = useResendGate(resendAfter, startedAt)
  const onExpire = useCallback(() => setExpired(true), [])

  function applyChallengeTiming(payload) {
    const { expiresIn: exp, resendAfter: ra } = pickTiming(payload)
    setExpiresIn(exp)
    setResendAfter(ra)
    setStartedAt(Date.now())
    setExpired(false)
  }

  async function requestCode(e) {
    e?.preventDefault()
    const nextErrors = {}
    if (!email.trim()) nextErrors.email = 'Email is required'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return

    setAlert(null)
    setBusy(true)
    try {
      const res = await forgetPassword(email.trim())
      applyChallengeTiming(res)
      setCode('')
      setStep(2)
      setAlert({
        type: 'success',
        message: res?.detail || res?.message || 'We sent a 6-digit code to your email.',
      })
    } catch (err) {
      setAlert({ type: 'danger', message: authErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(e) {
    e.preventDefault()
    const nextErrors = {}
    if (!/^\d{6}$/.test(code)) nextErrors.code = 'Enter the 6-digit code'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    if (expired) {
      setAlert({ type: 'danger', message: 'That code has expired. Request a new one.' })
      return
    }

    setAlert(null)
    setBusy(true)
    try {
      const res = await verifyForgetCode(email.trim(), code)
      const token = pickResetToken(res)
      if (!token) {
        setAlert({ type: 'danger', message: 'Verification succeeded but no reset token was returned.' })
        return
      }
      setResetToken(token)
      setStep(3)
      setAlert(null)
    } catch (err) {
      setAlert({ type: 'danger', message: authErrorMessage(err, 'Invalid or expired code.') })
    } finally {
      setBusy(false)
    }
  }

  async function submitPassword(e) {
    e.preventDefault()
    const nextErrors = {}
    if (!password) nextErrors.password = 'Password is required'
    else if (password.length < 8) nextErrors.password = 'Use at least 8 characters'
    if (confirm !== password) nextErrors.confirm = 'Passwords do not match'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return

    setAlert(null)
    setBusy(true)
    try {
      await setNewPassword(password, resetToken)
      setAlert({
        type: 'success',
        message: 'Password updated. You can sign in with your new password.',
      })
      setStep(4)
    } catch (err) {
      setAlert({ type: 'danger', message: authErrorMessage(err, 'Could not update password.') })
    } finally {
      setBusy(false)
    }
  }

  const titles = {
    1: 'Forgot password',
    2: 'Enter code',
    3: 'New password',
    4: 'Password updated',
  }
  const subtitles = {
    1: 'We’ll email you a one-time code to reset access.',
    2: `Enter the 6-digit code sent to ${email}.`,
    3: 'Choose a new password for your account.',
    4: 'You’re all set.',
  }

  return (
    <AuthLayout
      title={titles[step]}
      subtitle={subtitles[step]}
      foot={
        step === 4 ? (
          <Link className="link-btn" to="/auth/login">
            Back to sign in
          </Link>
        ) : (
          <>
            Remembered it?{' '}
            <Link className="link-btn" to="/auth/login">
              Sign in
            </Link>
          </>
        )
      }
    >
      <Alert type={alert?.type}>{alert?.message}</Alert>

      {step === 1 && (
        <form onSubmit={requestCode} noValidate>
          <div className="form-field">
            <label htmlFor="fp-email">
              Email<span className="req"> *</span>
            </label>
            <input
              id="fp-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setErrors((prev) => {
                  if (!prev.email) return prev
                  const n = { ...prev }
                  delete n.email
                  return n
                })
              }}
              className={errors.email ? 'err' : ''}
              placeholder="you@utopiabrands.com"
              disabled={busy}
              required
            />
            <span className={`field-error${errors.email ? ' show' : ''}`}>{errors.email || ''}</span>
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner label="Sending code" /> : null}
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verifyCode} noValidate>
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v)
              setErrors((prev) => {
                if (!prev.code) return prev
                const n = { ...prev }
                delete n.code
                return n
              })
            }}
            disabled={busy}
            error={Boolean(errors.code)}
          />
          <span className={`field-error${errors.code ? ' show' : ''}`}>{errors.code || ''}</span>

          <Countdown expiresIn={expiresIn} startedAt={startedAt} onExpire={onExpire} />

          <button type="submit" className="btn btn-primary btn-block" disabled={busy || expired}>
            {busy ? <Spinner label="Verifying" /> : null}
            {busy ? 'Verifying…' : 'Verify code'}
          </button>

          <div className="auth-resend">
            {resendLeft > 0 ? (
              <span className="countdown">Resend available in {Math.ceil(resendLeft)}s</span>
            ) : (
              <button
                type="button"
                className="link-btn"
                disabled={busy}
                onClick={() => requestCode()}
              >
                Resend code
              </button>
            )}
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={submitPassword} noValidate>
          <PasswordField
            id="fp-password"
            label="New password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            error={errors.password}
            disabled={busy}
          />
          <PasswordField
            id="fp-confirm"
            label="Confirm password"
            autoComplete="new-password"
            value={confirm}
            onChange={setConfirm}
            error={errors.confirm}
            disabled={busy}
          />
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner label="Saving password" /> : null}
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </form>
      )}

      {step === 4 && (
        <Link className="btn btn-primary btn-block" to="/auth/login" style={{ textDecoration: 'none' }}>
          Sign in
        </Link>
      )}
    </AuthLayout>
  )
}
